import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {CollapsibleFeedbackLink} from '@github-ui/repos-issues-sidebar/CollapsibleFeedbackLink'
import {SIDEBAR_STORAGE_KEY} from '@github-ui/repos-issues-sidebar/constants'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import {PageHeader} from '@primer/react'
import type {ReactNode} from 'react'

import {useRepoIssuesSidebarPanes} from './use-repo-issues-sidebar-panes'

const SIDEBAR_ARIA_LABEL = 'Issues sidebar navigation'
const DEFAULT_SIDEBAR_DIALOG_TITLE = 'Issues'

export interface RepoIssuesPageLayoutProps {
  children: ReactNode
  /** Page title for the header. Pass `null` (or omit when no actions either) to suppress the header. */
  title?: ReactNode
  /** Trailing actions for the page header (typically a primary CTA). */
  actions?: ReactNode
  /** Title for the narrow-viewport sidebar Dialog. */
  dialogTitle?: string
  /** Render the sidebar content. Receives `true` when the sidebar is collapsible. */
  renderSidebar: (collapsible?: boolean) => ReactNode
}

/**
 * Layout wrapper for every repo-scoped issues page. When
 * `repo_issues_sidebar_layout` is on, renders the new
 * `<FilteredListPageLayout>` (a `SidebarPageLayout`-composed template that
 * additionally provides `FilterBar` / `Results` / `HeaderActions` slots —
 * page-level slots are adopted by `IssueIndexPage` and the migrated
 * Labels/Milestones/Projects pages). An optional `<PageHeader>` is rendered
 * if a `title` or `actions` prop is passed. When the flag is off, falls
 * back to the legacy `<ThreePanesLayout>` via `useRepoIssuesSidebarPanes()`
 * (which itself respects `issue_pinned_views` and
 * `issues_react_collapsible_sidebar` for the legacy sidebar variants).
 */
export function RepoIssuesPageLayout({
  children,
  title,
  actions,
  dialogTitle = DEFAULT_SIDEBAR_DIALOG_TITLE,
  renderSidebar,
}: RepoIssuesPageLayoutProps) {
  const useSidebarPageLayout = isFeatureEnabled('repo_issues_sidebar_layout')
  const showHeader = useSidebarPageLayout && (title != null || actions != null)

  // Always call the hook so React sees the same hook order on every render,
  // regardless of which branch we take below.
  const panes = useRepoIssuesSidebarPanes(renderSidebar)

  if (useSidebarPageLayout) {
    return (
      <FilteredListPageLayout storageKey={SIDEBAR_STORAGE_KEY}>
        {showHeader ? (
          <FilteredListPageLayout.Header>
            <PageHeader>
              {title != null && (
                <PageHeader.TitleArea>
                  <PageHeader.Title as="h1">{title}</PageHeader.Title>
                </PageHeader.TitleArea>
              )}
              {actions != null && <PageHeader.Actions>{actions}</PageHeader.Actions>}
            </PageHeader>
          </FilteredListPageLayout.Header>
        ) : null}
        <FilteredListPageLayout.Sidebar
          aria-label={SIDEBAR_ARIA_LABEL}
          dialogTitle={dialogTitle}
          footer={({isExpanded}) => (
            <>
              <CollapsibleFeedbackLink isExpanded={isExpanded} />
              <CollapsibleSidebar.Toggle />
            </>
          )}
        >
          {renderSidebar(true)}
        </FilteredListPageLayout.Sidebar>
        <FilteredListPageLayout.Content as="div">{children}</FilteredListPageLayout.Content>
      </FilteredListPageLayout>
    )
  }

  return (
    <ThreePanesLayout
      contentAs="div"
      resizeable={false}
      leftPaneWidth="medium"
      leftPane={panes.leftPane}
      collapsibleLeftPane={panes.collapsibleLeftPane}
      middlePane={<>{children}</>}
    />
  )
}
